import type { TFunction } from "i18next";
import jsep from "jsep";
import type {
  FormulaData,
  FormulaTemplateEntry
} from "../FormFields/ExpressionEditor/ExpressionEditor.types";

function translateErrorMessage(msg: string, t: TFunction) {
  let matches: RegExpExecArray | null;

  if (
    (matches = /Expected expression after (.*) at character (\d+)/.exec(msg))
  ) {
    return t("errors.ExpressionEditor.MissingExpressionAfterCharacter", {
      character: matches[1],
      position: matches[2]
    });
  }

  if ((matches = /Expected (.*) at character (\d+)/.exec(msg))) {
    let expectationString: string;
    switch (matches[1]) {
      case "comma":
        expectationString = "Komma";
        break;

      case "expression":
        expectationString = "Ausdruck";
        break;

      default:
        expectationString = matches[1];
    }

    return t("errors.ExpressionEditor.MissingElement", {
      expectedElement: expectationString,
      position: matches[2]
    });
  }

  if ((matches = /Unexpected (?:token )?(.*) at character (\d+)/.exec(msg))) {
    return t("errors.ExpressionEditor.UnexpectedElement", {
      unexpectedElement: matches[1],
      position: matches[2]
    });
  }

  if ((matches = /Unclosed \( at character (\d+)/.exec(msg))) {
    return t("errors.ExpressionEditor.ParenthesisNotClosed", {
      position: matches[1]
    });
  }

  if ((matches = /missing unaryOp argument at character (\d+)/.exec(msg))) {
    return t("errors.ExpressionEditor.MissingNumberOrVariable", {
      position: matches[1]
    });
  }

  return t("errors.ExpressionEditor.NonSpecificError");
}

export interface ValidateExpressionParams {
  value: FormulaData;
  templates?: Array<FormulaTemplateEntry>;
  required: boolean;
  t: TFunction;
}

export function validateExpression({
  value,
  templates,
  required,
  t
}: ValidateExpressionParams) {
  // TODO in https://node-energy.atlassian.net/browse/WUP-8864: remove the fallback remapping after BE integrates new API
  if (typeof value === "string")
    value = { formula: (value as unknown as string) ?? "" };

  if (!value || !value.formula) {
    return required ? t("errors.Required") : true;
  }

  if (value.template_id && templates) {
    const template = templates.find((t) => t.id === value.template_id);
    if (
      template &&
      template.custom_value_names &&
      template.custom_value_names.length > 0
    ) {
      if (!value.custom_values) {
        return t("errors.ExpressionEditor.CustomValuesMissing");
      }

      for (const customValueName of template.custom_value_names) {
        if (!value.custom_values[customValueName]) {
          return t("errors.ExpressionEditor.CustomValuesMissing");
        }
      }
    }
  }

  try {
    jsep(value.formula);
  } catch (exception) {
    return translateErrorMessage(exception.message, t);
  }

  return true;
}
